import { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { useSearchParams } from 'react-router-dom'
import { PageLink, PageTitle } from '../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../_metronic/helpers'
import { SelectStyles } from '../../../../../components/Select'
import { useThemeMode } from '../../../../../_metronic/partials'
import { getAdapterRoutingCoverage } from '../../api/getAdapterRoutingCoverage'
import { postPriorityConfig } from '../../api/postPriorityConfig'
import { getListMerchantsForSelect } from '../../../../common/core/_requests'
import { IsGranted, useAuth } from '../../../auth'
import {
  findGaps,
  summarizeHealth,
  sortConfigurations,
  type CoverageResponse,
  type CoverageEntry,
  type Gap,
  type HealthSummary,
} from './model/coverage'
import { encodeContextId } from './model/context'
import { HealthSummaryBar, type HealthFilter } from './components/HealthSummaryBar'
import { GapsBlock } from './components/GapsBlock'
import { CoverageList } from './components/CoverageList'
import { NewConfigDialog } from './components/NewConfigDialog'
import { STATUS_LABEL } from './labels'
import { ADAPTER_PRIORITY_WRITE_ROLES } from '../../core/adapterPriorityAccess'

const breadCrumbs: Array<PageLink> = [
  {
    title: 'Карта покрытия маршрутизации',
    path: '/settings/adapter-routing',
    isSeparator: false,
    isActive: true,
  },
]

type Merchant = { token: string; title: string }

/**
 * Экран A — Карта покрытия маршрутизации. Merchant-first: the coverage endpoint
 * (GET /priority/coverage) is per-merchant, so the operator picks a merchant and
 * we load that merchant's whole routing surface — health counters (click to
 * filter), gaps panel, drag-reorderable list. Manual reorder and the
 * disabled-bottom rule live on the client.
 */
const CoverageMapPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [merchants, setMerchants] = useState<Merchant[]>([])
  // Selected merchant is mirrored in the URL (?merchant=<token>) so it survives
  // navigation to the config editor and back — and a page reload (CPAYMENT-1485).
  const [merchantToken, setMerchantToken] = useState<string | null>(() => searchParams.get('merchant'))
  const [data, setData] = useState<CoverageResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [filter, setFilter] = useState<HealthFilter>(null)
  /** Client-side manual order overlay. Keyed by contextId. */
  const [manualOrder, setManualOrder] = useState<Record<string, number>>({})
  const [newConfigOpen, setNewConfigOpen] = useState(false)
  const { mode: theme } = useThemeMode()
  const { currentUser } = useAuth()
  // the merchant payments operator reads the map; creating and reordering configs is refused by the backend
  const canWrite = IsGranted(ADAPTER_PRIORITY_WRITE_ROLES, currentUser)

  // Select a merchant and keep the URL in sync so the choice is restored on return.
  const selectMerchant = (token: string | null) => {
    setMerchantToken(token)
    const next = new URLSearchParams(searchParams)
    if (token) next.set('merchant', token)
    else next.delete('merchant')
    setSearchParams(next, { replace: true })
  }

  // Full merchant list once — the primary navigation of this merchant-first page.
  // Role-aware source: employees/managers get the internal list, operators get
  // their own merchants (/api/merchant/list) — avoids the 403 on /merchant/list.
  useEffect(() => {
    let alive = true
    getListMerchantsForSelect(currentUser)
      .then(({ data }) => {
        if (!alive) return
        const list = (data?.merchants ?? [])
          .filter((m) => m.token)
          .map((m) => ({ token: m.token, title: m.title || m.token }))
        setMerchants(list)
      })
      .catch(() => {
        /* selector stays empty; page shows the prompt */
      })
    return () => {
      alive = false
    }
  }, [currentUser])

  // Coverage for the selected merchant.
  useEffect(() => {
    if (!merchantToken) {
      setData(null)
      return
    }
    let alive = true
    setIsLoading(true)
    setIsError(false)
    setFilter(null)
    getAdapterRoutingCoverage(merchantToken, currentUser)
      .then((res) => {
        if (!alive) return
        setData(res)
        const seed: Record<string, number> = {}
        res.configurations.forEach((c) => {
          if (typeof c.order === 'number') seed[encodeContextId(c.context)] = c.order
        })
        setManualOrder(seed)
      })
      .catch(() => alive && setIsError(true))
      .finally(() => alive && setIsLoading(false))
    return () => {
      alive = false
    }
  }, [merchantToken, currentUser])

  const merchantOptions = useMemo(
    () => merchants.map((m) => ({ value: m.token, label: m.title })),
    [merchants]
  )
  const merchantTitle = useMemo(() => {
    const map = new Map(merchants.map((m) => [m.token, m.title]))
    return (token: string) => map.get(token) ?? token
  }, [merchants])

  // Apply manual order overlay, then the canonical sort (disabled forced bottom).
  const sortedConfigs = useMemo<CoverageEntry[]>(() => {
    if (!data) return []
    const withOrder = data.configurations.map((c) => ({
      ...c,
      order: manualOrder[encodeContextId(c.context)] ?? c.order,
    }))
    return sortConfigurations(withOrder)
  }, [data, manualOrder])

  const gaps = useMemo<Gap[]>(
    () => (data ? findGaps(data.expectedContexts, data.configurations) : []),
    [data]
  )

  // Anti-dup set for «Новая конфигурация» — open existing instead of duplicating.
  const existingContextIds = useMemo<Set<string>>(
    () => new Set((data?.configurations ?? []).map((c) => encodeContextId(c.context))),
    [data]
  )

  const health = useMemo<HealthSummary | null>(
    () => (data ? summarizeHealth(sortedConfigs, gaps) : null),
    [data, sortedConfigs, gaps]
  )

  // Status filter narrows the list. GAPS bucket = configs with no working adapter.
  const filteredConfigs = useMemo(() => {
    if (filter === null) return sortedConfigs
    if (filter === 'GAPS') return sortedConfigs.filter((c) => c.status === 'EMPTY')
    return sortedConfigs.filter((c) => c.status === filter)
  }, [sortedConfigs, filter])

  const showGaps = filter === null || filter === 'GAPS'
  const showList = filter !== 'GAPS' || filteredConfigs.length > 0

  // New-config dialog uses the per-merchant dimensions but the FULL merchant list
  // so the operator can target any merchant.
  const dialogDimensions = useMemo(
    () => (data ? { ...data.dimensions, merchants } : null),
    [data, merchants]
  )

  const handleReorder = (next: CoverageEntry[]) => {
    // Immediate client-side overlay so the list re-orders without a round-trip.
    setManualOrder((prev) => {
      const updated = { ...prev }
      next.forEach((entry, index) => {
        updated[encodeContextId(entry.context)] = index
      })
      return updated
    })
    // CPAYMENT-1485: persist the new manual order so it survives a reload.
    // Drag is only enabled with no active filter, so `next` is the full set;
    // each context's index becomes its stored `order` (enabled flag preserved).
    Promise.allSettled(
      next.map((entry, index) =>
        postPriorityConfig({
          merchantToken: entry.context.merchantToken,
          paymentMethodType: entry.context.paymentMethodType,
          currency: entry.context.currency,
          direction: entry.context.direction,
          externalUserFlag: entry.context.externalUserFlag,
          source: entry.context.source,
          enabled: entry.enabled,
          order: index,
        })
      )
    ).then((results) => {
      const failed = results.some(
        (r) => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.data?.success)
      )
      if (failed) {
        // eslint-disable-next-line no-console
        console.error('CPAYMENT-1485: failed to persist coverage configuration order')
      }
    })
  }

  return (
    <div>
      <PageTitle breadcrumbs={breadCrumbs} />

      <div className='card mb-7'>
        <div className='card-header border-0 pt-6'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Карта покрытия маршрутизации</span>
            <span className='text-muted fw-semibold fs-7'>
              Маршрутная конфигурация мерчанта: что настроено, что требует внимания, чего не хватает.
            </span>
          </h3>
        </div>
        <div className='card-body pt-2'>
          <div className='d-flex flex-wrap align-items-end justify-content-between gap-3 mb-5'>
            <div style={{ maxWidth: 360, flexGrow: 1 }}>
              <label className='form-label fw-semibold fs-7 text-gray-700'>Мерчант</label>
              <Select
                options={merchantOptions}
                styles={SelectStyles(theme)}
                placeholder='Выберите мерчанта'
                value={
                  merchantToken
                    ? { value: merchantToken, label: merchantTitle(merchantToken) }
                    : null
                }
                onChange={(opt) => selectMerchant(opt?.value ?? null)}
              />
            </div>
            {canWrite && (
              <button
                type='button'
                className='btn btn-primary'
                disabled={!data}
                onClick={() => setNewConfigOpen(true)}
              >
                <KTIcon iconName='plus' className='fs-3 me-1' />
                Новая конфигурация
              </button>
            )}
          </div>

          {!merchantToken && (
            <div className='text-gray-600 py-6'>
              Выберите мерчанта, чтобы увидеть его карту покрытия маршрутизации.
            </div>
          )}
          {isLoading && <div className='text-gray-600 py-6'>Загрузка карты покрытия…</div>}
          {isError && (
            <div className='alert alert-danger'>Не удалось загрузить карту покрытия.</div>
          )}
          {data && health && (
            <HealthSummaryBar health={health} active={filter} onSelect={setFilter} />
          )}
        </div>
      </div>

      {data && health && (
        <>
          {showGaps && (
            <div className='card mb-7'>
              <div className='card-body'>
                <GapsBlock gaps={gaps} merchantTitle={merchantTitle} canWrite={canWrite} />
              </div>
            </div>
          )}

          {showList && (
            <div className='card'>
              <div className='card-header border-0 pt-6 d-flex align-items-center'>
                <h3 className='card-title fw-bold fs-4'>Конфигурации</h3>
                {filter && (
                  <div className='card-toolbar'>
                    <span className='text-muted fs-7 me-3'>
                      Фильтр: {filter === 'GAPS' ? 'Требуют настройки' : STATUS_LABEL[filter]}
                    </span>
                    <button
                      type='button'
                      className='btn btn-sm btn-light'
                      onClick={() => setFilter(null)}
                    >
                      Все конфигурации
                    </button>
                  </div>
                )}
              </div>
              <div className='card-body pt-4'>
                <CoverageList
                  configurations={filteredConfigs}
                  merchantTitle={merchantTitle}
                  onReorder={handleReorder}
                  dragDisabled={filter !== null || !canWrite}
                />
                {filter !== null && (
                  <div className='text-muted fs-8 mt-3'>
                    Перетаскивание доступно без фильтра — нажмите «Все конфигурации».
                  </div>
                )}
              </div>
            </div>
          )}

          {dialogDimensions && (
            <NewConfigDialog
              show={newConfigOpen}
              onClose={() => setNewConfigOpen(false)}
              dimensions={dialogDimensions}
              existingContextIds={existingContextIds}
              merchantFilter={merchantToken}
            />
          )}
        </>
      )}
    </div>
  )
}

export default CoverageMapPage
